<script lang="ts" setup>
import EllipsisIcon from "@/components/icon/EllipsisIcon.vue";
import { showImagePreview, showLoadingToast, Tag } from "vant";
import { computed, onActivated, ref, toRefs } from "vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { useDefaultAvatar, useMateTypeText, usePreviewImg } from "@/use/common";
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";
import { useRouter } from "vue-router";
import { isImage, timeAgo } from "@/modules/common";
import AudioPlay from "@/components/mobile/AudioPlay.vue";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import ReactionMate from "@/components/mobile/reply/ReactionMate.vue";
import BookIcon from "@/components/icon/BookIcon.vue";
import BaseImg from "./common/BaseImg.vue";
import escapeHtml from "@/modules/escape-html";
import TextEllipsis from "@/components/mobile/text-ellipsis/TextEllipsis.vue";
import { useGlobalUser } from "@/stores/globalUser";
import { GAL_GAME_URL_PREFIX } from "@/stores/storyGameEditor";
import { useMate } from "@/stores/mate";
import { CoinBalanceRecordEntityType } from "@/types/coin";
import { useI18n } from "vue-i18n";
import { ApiPostCollectionItemSortMany } from "@/apis/collection";
import { CollectionItem } from "@/types/collection";
import { Moment } from "@/types/entity";

// Props & Emit
interface ThreadItemProps {
  collectionItem: CollectionItem;
  preItem?: CollectionItem;
  nextItem?: CollectionItem;
  moment: Moment;
  showComment?: boolean;
  enableReactionBtn?: boolean;
  replyTo?: Reply;
  enableTextCollapsed?: boolean;
  enableAudioPlay?: boolean;
  enableShowMoreLink?: boolean; // 串文：hasNext为true时，显示查看更多按钮
  showAvatar?: boolean; // 是否显示头像
  matchedText?: string; // 需要高亮的文本
  showCoinVote?: boolean;
  viewMode: string;
  list: CollectionItem[];
  idx: number;
}

const props: ThreadItemProps = withDefaults(defineProps<ThreadItemProps>(), {
  showComment: true,
  enableReactionBtn: true,
  enableTextCollapsed: true,
  enableAudioPlay: false,
  enableShowMoreLink: false,
  showAvatar: true,
  showCoinVote: false,
});

const { t } = useI18n();

const {
  id,
  chatMessagezz,
  mate,
  text,
  mediazz,
  uuid,
  childId,
  parentId,
  mentionMatezz,
  fromMate,
  reply,
} = props.moment;

const hasNext = !!childId;
const hasPrev = !!parentId;

const emit = defineEmits<{
  (e: "threadClick", uuid: string): void;
  (e: "commentClick"): void;
  (e: "reactionClick", moment: Moment): void;
}>();
const messageList = computed(() => {
  if (!chatMessagezz || chatMessagezz.length === 0) {
    return [];
  }
  return chatMessagezz.map((item) => {
    return {
      id: item.id,
      currentUser: item.mate.uuid === mate.uuid,
      avatarUrl: item.mate.mediaUrl,
      msg: item.text,
      name: item.mate.name,
    };
  });
});
const router = useRouter();
const user = useGlobalUser();
const escapedContent = computed(() => {
  if (!text) {
    return "";
  }
  return escapeHtml(text);
});
const displayContent = computed(() => {
  if (!text) {
    return "";
  }
  // 1. 先按照换行符分割，保留7行
  const splitByLine = escapedContent.value.split("\n").slice(0, 7);
  // 2. 再按照字符数分割，保留前100个字符
  return splitByLine.join("\n").slice(0, 128);
});

const needCollapsed =
  props.enableTextCollapsed! &&
  !!text &&
  text!.length !== displayContent.value.length;

const textCollapsed = ref<boolean>(needCollapsed);

const contentHtml = computed(() => {
  if (!text) {
    return "";
  }

  const content = escapedContent.value.replace(/\n/g, "<br/>");
  if (props.matchedText) {
    const reg = new RegExp(props.matchedText, "g");
    return content.replace(reg, (matched) => {
      return `<span class="bg-yellow-500 text-neutral">${matched}</span>`;
    });
  }
  return content;
});

const isSingleLongImgMedia = computed(() => {
  return (
    mediazz &&
    mediazz.length === 1 &&
    (mediazz[0].type === 1 || isImage(mediazz[0].url)) &&
    mediazz[0].height > mediazz[0].width
  );
});

const coinVoteInfo = computed(() => {
  let type = CoinBalanceRecordEntityType.Mate;
  let item: Mate | StoryBook | GalGame = props.moment.mate;
  if (props.moment.galGame) {
    type = CoinBalanceRecordEntityType.Game;
    item = props.moment.galGame;
  } else if (props.moment.story) {
    type = CoinBalanceRecordEntityType.StoryBook;
    item = props.moment.story.storyBook!;
  }
  return { type, item };
});

const donateInfo = computed(() => {
  let type = "";
  let item: any = {};
  if (props.moment.galGame) {
    type = "Game";
    item = props.moment.galGame;
  } else if (props.moment.story) {
    type = "StoryBook";
    item = props.moment.story;
  } else {
    type = "Moment";
    item = props.moment;
  }
  return { type, item };
});

// Event Handler
const chatShareClick = (e: UIEvent) => {
  e.stopPropagation();
  router.push({ name: "shareChatPreview", params: { uuid } });
};
const onImageClick = (
  e: UIEvent,
  mediaList: Media[] | MomentMedia[],
  idx: number
) => {
  e.stopPropagation();
  showImagePreview({
    images: mediaList.map((item) => item.url),
    closeable: true,
    startPosition: idx,
  });
};
const onReactionClick = (e: UIEvent) => {
  e.stopPropagation();
  toVoteReply.value = props.moment!.reply;
  emit("reactionClick", props.moment);
};

const routeToMateHome = (e: UIEvent, mateUuid: string) => {
  e.stopPropagation();
  router.push({ name: "mateHome", params: { uuid: mateUuid } });
};
const onStoryClick = (e: UIEvent) => {
  e.stopPropagation();
  router.push({
    name: "readStory",
    params: { uuid: props.moment!.story.uuid },
  });
};

const toMateDetail = (uuid: string) => {
  router.push({ name: "mateHome", params: { uuid: uuid } });
};

const clickOpenGame = (uuid: string, nodeId: number) => {
  window.open(
    GAL_GAME_URL_PREFIX + `?uuid=${uuid}&nodeId=${nodeId}&type=story`
  );
};
const onThreadClick = () => {
  if (childId) {
    router.push({
      name: "momentChain",
      query: {
        rootId: id,
        mateId: mate!.id,
      },
    });
  } else {
    const { collectableType, collectable } = props.collectionItem;
    if (collectableType === "moment") {
      router.push({
        name: "threadDetail",
        params: { uuid },
      });
    } else if (collectableType === "storyBook") {
      router.push({
        name: "storyBookHome",
        params: { uuid: collectable?.uuid },
      });
    } else if (collectableType === "galGame") {
      router.push({
        name: "galGameDetail",
        params: { uuid: collectable?.uuid },
      });
    }
  }
};

onActivated(() => {
  textCollapsed.value = needCollapsed;
});

const toVoteReply = ref<Reply | null>(null);

const emojiShow = ref<boolean>(false);
// EventHandler
const triggerVote = () => {
  toVoteReply.value = reply ?? props.moment!.reply;
  emojiShow.value = true;
};

const mateStore = useMate();
const showMomentDelete = computed(
  () =>
    props.moment &&
    mateStore.currentMate?.id === props.moment.mate?.id &&
    (!props.moment.childId || !props.moment.parentId)
);
const showReplyDelete = computed(
  () => reply && mateStore.currentMate?.id === reply.mate?.id
);

const clickSort = async (direction: "up" | "down") => {
  const loading = showLoadingToast({ message: t("Message.loading") });
  await ApiPostCollectionItemSortMany(props.collectionItem.id!, { direction });
  const { list, idx } = props;

  if (direction === "up") list.splice(idx - 1, 0, list.splice(idx, 1)[0]);
  else list.splice(idx + 1, 0, list.splice(idx, 1)[0]);

  loading.close();
};
</script>
<template>
  <div
    :class="{
      'pb-0': hasNext && !enableShowMoreLink,
      'pt-0': hasPrev,
      'border-b': !hasNext || enableShowMoreLink,
    }"
    class="flex flex-col w-full py-2"
    @click="onThreadClick"
  >
    <div class="flex gap-2">
      <!-- Avatar -->
      <div
        v-if="showAvatar"
        :class="{ 'has-next': hasNext, 'has-prev': hasPrev }"
        class="flex flex-col items-center"
      >
        <user-avatar
          :avatar-url="mate.mediaUrl"
          class="h-10 shrink-0"
          @click="(e) => routeToMateHome(e, mate.uuid)"
        />
      </div>
      <!--  标题 & 类型 & 时间  -->
      <div
        :class="{ 'show-more-placeholder': hasPrev }"
        class="flex-grow flex flex-col overflow-hidden"
      >
        <div
          class="flex flex-col flex-grow gap-3"
          @click="emit('threadClick', uuid!)"
        >
          <div class="flex-grow">
            <div class="flex justify-between gap-1">
              <div class="flex flex-col">
                <div class="flex flex-col">
                  <div
                    v-if="mate.name"
                    class="text-base-content font-bold"
                    @click="(e) => routeToMateHome(e, mate.uuid)"
                  >
                    {{ mate.name }}
                  </div>
                </div>
                <div v-if="mate.type" class="text-xs text-base-content/50">
                  {{ useMateTypeText(mate.type) }}
                </div>
                <div v-if="replyTo" class="text-xs">
                  {{ $t("Reply.reply") }}
                  <span
                    class="text-base-content/50"
                    @click="(e) => routeToMateHome(e, replyTo!.mate.uuid)"
                  >
                    @{{ replyTo.mate.name }}
                  </span>
                </div>
              </div>
              <div class="flex flex-col text-base-content/50 text-xs">
                <template v-if="viewMode === 'edit'">
                  <template
                    v-if="Number(new Date(collectionItem.dtCreate!)) -
                          Number(new Date(collectionItem.collectable!.dtCreate!))>
                        0"
                  >
                    <div>
                      {{
                        $t(`collection.collectionTime`) +
                        timeAgo(collectionItem.dtCreate!)
                      }}
                    </div>
                    <div>
                      {{
                        $t(`collection.publishTime`) +
                        timeAgo(collectionItem.collectable!.dtCreate!)
                      }}
                    </div>
                  </template>
                  <div v-else>
                    {{
                      $t(`collection.c_pTime`) +
                      timeAgo(collectionItem.dtCreate!)
                    }}
                  </div>
                </template>
                <div v-else>
                  {{ timeAgo(collectionItem.collectable!.dtCreate!) }}
                </div>
              </div>
            </div>
            <!-- 文本内容 -->
            <div
              v-if="text && !matchedText"
              class="text-base-content whitespace-pre-line"
            >
              <text-ellipsis
                v-if="props.enableTextCollapsed"
                :collapse-text="$t('Buttons.collapse')"
                :content="text"
                :expand-text="$t('Buttons.viewAll')"
                :rows="5"
                class="pr-8"
                @click-action="$event.stopPropagation()"
              />
              <div v-else class="whitespace-pre-line break-words">
                {{ text }}
              </div>
              <div
                v-if="
                  user.isLogin &&
                  enableAudioPlay &&
                  text &&
                  mate.mateToneId &&
                  mate.mateToneId > 0
                "
                class="flex justify-end -mt-6"
              >
                <audio-play :mate-tone-id="mate.mateToneId!" :msg="text" />
              </div>
              <div
                v-if="mentionMatezz && mentionMatezz.length > 0"
                class="mt-2 flex gap-2 flex-wrap"
              >
                <Tag
                  v-for="mentionMate in mentionMatezz"
                  :key="mentionMate.uuid"
                  class="badge badge-neutral"
                  @click.stop="toMateDetail(mentionMate.uuid)"
                >
                  @{{ mentionMate.name }}
                </Tag>
              </div>
            </div>
            <div
              v-else-if="text && matchedText"
              class="text-base-content whitespace-pre-line"
              v-html="contentHtml"
            ></div>
          </div>
          <!--  图片内容 -->
          <div
            v-if="mediazz && mediazz.length > 0"
            :class="{
              'grid-cols-[repeat(auto-fit,minmax(90px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]':
                !isSingleLongImgMedia,
              'grid-cols-2': isSingleLongImgMedia,
            }"
            class="grid gap-1"
          >
            <div
              v-for="(media, idx) in mediazz"
              :key="idx"
              class="rounded overflow-hidden"
              @click.stop="(e) => onImageClick(e, mediazz!, idx)"
            >
              <BaseImg
                v-if="media.type === 1 || isImage(media.url)"
                :media="media"
                :width="
                  mediazz.length >= 3
                    ? 300
                    : mediazz.length === 2
                    ? 400
                    : media.width
                "
                class="w-full h-full rounded overflow-hidden"
              />
              <video
                v-else
                :poster="media.videoCoverUrl ?? ''"
                :src="media.url"
                class="max-w-full max-h-52"
                controls
                crossorigin="anonymous"
              />
            </div>
          </div>

          <!-- 故事内容 -->
          <div v-if="moment?.story" class="flex flex-col gap-2">
            <div v-if="moment.storyGame">
              <div
                class="relative h-[180px]"
                @click.stop="
                  clickOpenGame(
                    moment.storyGame.uuid!,
                    moment.story.storyGameStoryNodeId!
                  )
                "
              >
                <VanImage
                  :src="
                    moment.story.media
                      ? usePreviewImg(moment.story.media.url)
                      : useDefaultAvatar()
                  "
                  alt=""
                  class="absolute top-0 left-0 w-full h-[180px] rounded"
                  fit="cover"
                  style="filter: blur(3px)"
                  crossorigin="anonymous"
                />
                <VanImage
                  :src="
                    moment.story.media
                      ? usePreviewImg(moment.story.media.url)
                      : useDefaultAvatar()
                  "
                  alt=""
                  class="absolute top-0 left-1/2 -translate-x-1/2 w-[180px] h-[180px] rounded"
                  fit="cover"
                  crossorigin="anonymous"
                />
                <button
                  class="btn btn-sm btn-primary absolute bottom-2.5 right-2.5"
                >
                  {{ $t(`Buttons.play`) }}
                </button>
              </div>
            </div>
            <div
              v-if="
                (moment.mediazz &&
                  moment.mediazz[0] &&
                  moment.mediazz[0].type === 2) ||
                moment.storyGame
              "
              @click.stop="onStoryClick"
            >
              <div class="flex flex-col flex-grow gap-1 h-full overflow-hidden">
                <div class="text-base">
                  {{ moment?.story.title }}
                </div>
                <div class="text-base-content/50 text-sm line-clamp-3">
                  {{ moment?.story.description }}
                </div>
              </div>
            </div>
            <story-list-item
              v-else
              :story="moment.story"
              class="border px-2 rounded"
              @click.stop="onStoryClick"
            />

            <div v-if="moment.story?.storyBook" class="flex gap-1 items-center">
              <div class="text-xs text-base-content/50 whitespace-nowrap">
                {{ $t("story.collectedOn") }}
              </div>
              <div
                class="badge text-xs gap-1 cursor-pointer flex"
                @click.stop="
                  $event.stopPropagation();
                  router.push({
                    name: 'storyBookHome',
                    params: {
                      uuid: moment.story?.storyBook?.uuid,
                    },
                  });
                "
              >
                <book-icon class="icon-xs" />
                <span class="flex-1 line-clamp-1">{{
                  moment.story?.storyBook?.title
                }}</span>
              </div>
            </div>
          </div>

          <div
            v-if="collectionItem.collectableType === 'storyBook'"
            class="flex flex-col gap-2"
          >
            <div class="flex gap-2">
              <div class="w-[100px]">
                <BaseImg
                  :media="collectionItem.collectable?.media"
                  ratio="1:1"
                />
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-medium">
                  {{ collectionItem.collectable?.title }}
                </h3>

                <p class="line-clamp-4">
                  {{ collectionItem.collectable?.description }}
                </p>
              </div>
            </div>
          </div>
          <!-- 游戏内容 -->
          <CardGalGameHome
            v-if="moment?.galGame"
            :key="moment.galGame.uuid"
            :game="moment.galGame"
            :show-author="false"
            show-desc
          />
          <CardGalGameHome
            v-if="collectionItem.collectableType === 'galGame'"
            :key="collectionItem.collectable?.id!"
            :game="collectionItem.collectable"
            :show-author="false"
            show-desc
          />
          <!-- 聊天内容 -->
          <div
            v-if="messageList && messageList.length > 0"
            class="flex-col max-h-64 overflow-hidden border p-2 rounded relative thread-chat-share-item bg-base-200"
            @click.stop="chatShareClick"
          >
            <chat-message-item
              v-for="item in messageList.slice(0, 4)"
              :key="item.id"
              :avatar-url="item.avatarUrl"
              :msg="item.msg"
              :name="item.name"
              :position="item.currentUser ? 'end' : 'start'"
              class="flex-grow"
            />
          </div>

          <!-- 回复内容 -->
          <div
            v-if="fromMate"
            class="flex items-center text-xs text-base-content/50 gap-1"
          >
            <span>{{ $t("Moment.collaborativeCreation") }}</span>
            <span
              class="badge badge-primary badge-sm"
              @click="(e) => routeToMateHome(e, fromMate!.uuid)"
            >
              @{{ fromMate.name }}
            </span>
          </div>

          <!-- 交互 -->
          <div class="flex flex-col gap-2">
            <reaction-mate :reply="reply" />
            <div class="flex w-full justify-between items-end">
              <div class="flex gap-2 flex-wrap">
                <!-- <Donate
                  v-if="showCoinVote"
                  :item="donateInfo.item"
                  :type="donateInfo.type"
                  teleport="#main-view"
                />
                <coin-vote
                  v-if="showCoinVote"
                  :item="coinVoteInfo.item"
                  :type="coinVoteInfo.type"
                  teleport="#main-view"
                /> -->
                <button
                  class="btn btn-xs btn-neutral font-normal rounded"
                  @click.stop="triggerVote"
                >
                  <happy-face-icon />
                </button>
                <button
                  v-if="showComment"
                  class="btn btn-xs rounded"
                  @click="emit('commentClick')"
                >
                  {{ $t("Overview.comments") }}
                  <span v-if="reply && reply.replyCount > 0">
                    ({{ reply?.replyCount }})
                  </span>
                </button>
                <reaction-counter :reply="reply" />
              </div>
              <div v-if="viewMode === 'edit'" class="flex gap-1 items-center">
                <button
                  v-if="idx !== 0"
                  class="text-base-content/50 btn btn-sm btn-ghost rounded"
                  @click.stop="clickSort('up')"
                >
                  <i class="iconfont icon-up" />
                </button>

                <button
                  v-if="idx !== list.length - 1"
                  class="text-base-content/50 btn btn-sm btn-ghost rounded"
                  @click.stop="clickSort('down')"
                >
                  <i class="iconfont icon-down" />
                </button>
                <div @click.stop="onReactionClick">
                  <ellipsis-icon class="text-base-content/50" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 查看更多 -->
    <div v-if="hasNext && enableShowMoreLink" class="text-base-content/50">
      <div class="flex gap-4 items-center">
        <div class="flex flex-col items-center has-prev">
          <user-avatar
            :avatar-url="mate.mediaUrl"
            class="h-10 shrink-0"
            @click="(e) => routeToMateHome(e, mate.uuid)"
          />
        </div>
        <div
          class="text-primary show-more-placeholder"
          @click.stop="
            $event.stopPropagation();
            router.push({
              name: 'momentChain',
              query: {
                rootId: props.moment!.id,
                mateId: props.moment!.mate!.id,
              },
            });
          "
        >
          {{ $t("Moment.viewAllContent") }}
        </div>
      </div>
    </div>
  </div>

  <emoji-vote-popup v-model:show="emojiShow" :to-vote-reply="toVoteReply" />
</template>
<style scoped>
.has-next::after {
  content: "";
  display: block;
  width: 0;
  height: 100%;
  @apply border-l-secondary/40 border-l-2;
}

.has-prev::before {
  content: "";
  display: block;
  width: 0;
  height: 10px;
  @apply border-l-secondary/40 border-l-2 shrink-0;
}

.show-more-placeholder::before {
  content: "";
  display: block;
  width: 0;
  height: 10px;
}
</style>
